import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play, Lock, CheckCircle, ChevronRight, Award, Zap,
  BookOpen, Target, Leaf, Brain, Trophy, Star, Clock
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ─── Stage colour palettes ────────────────────────────────────────────────────
const STAGE_CONFIG = {
  Stage1_Capacity: {
    label: 'Stage 1 — Capacity',
    subtitle: 'Build your foundation',
    gradient: 'from-violet-600 to-purple-700',
    light: 'from-violet-50 to-purple-50',
    border: 'border-violet-200',
    accent: 'bg-violet-600',
    text: 'text-violet-700',
    badge: 'bg-violet-100 text-violet-700',
    ring: 'ring-violet-400',
    icon: Brain,
    total: 10
  },
  Stage2_Capability: {
    label: 'Stage 2 — Capability',
    subtitle: 'Work with others',
    gradient: 'from-blue-600 to-cyan-600',
    light: 'from-blue-50 to-cyan-50',
    border: 'border-blue-200',
    accent: 'bg-blue-600',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-700',
    ring: 'ring-blue-400',
    icon: Target,
    total: 9
  },
  Stage3_Leadership: {
    label: 'Stage 3 — Leadership',
    subtitle: 'Lead with purpose',
    gradient: 'from-amber-500 to-orange-600',
    light: 'from-amber-50 to-orange-50',
    border: 'border-amber-200',
    accent: 'bg-amber-600',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-700',
    ring: 'ring-amber-400',
    icon: Trophy,
    total: 6
  }
};

const TRACK_CONFIG = {
  PIQ: { label: 'Personal Intelligent Quotient (PIQ)', code: 'PIQ', color: 'from-rose-500 to-pink-600', light: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: Star, unlockAfter: 'S05' },
  AIQ: { label: 'Artificial Intelligent Quotient (AIQ)', code: 'AIQ', color: 'from-emerald-500 to-teal-600', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: Zap, unlockAfter: 'S15' },
  SQ: { label: 'Sustainability Quotient (SQ)', code: 'SQ', color: 'from-green-500 to-lime-600', light: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: Leaf, unlockAfter: 'S21' }
};

// ─── Circular Progress Ring ───────────────────────────────────────────────────
const ProgressRing = ({ pct = 0, size = 72, stroke = 6, gradient = 'from-violet-600 to-purple-700', locked = false }) => {
  const r = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * (pct / 100);
  const id = `grad-${Math.random().toString(36).slice(2)}`;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={locked ? '#d1d5db' : '#7c3aed'} />
          <stop offset="100%" stopColor={locked ? '#e5e7eb' : '#6366f1'} />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={locked ? '#f3f4f6' : '#ede9fe'} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`url(#${id})`} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
    </svg>
  );
};

// ─── Gate Badge ───────────────────────────────────────────────────────────────
const GateBadge = ({ gateCode, gateStatus, onClick }) => {
  const passed = gateStatus?.passed;
  const attempted = !!gateStatus;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition-all shadow-sm
        ${passed ? 'bg-green-50 border-green-400 text-green-700 hover:bg-green-100'
          : attempted ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
            : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'}`}
    >
      {passed ? <CheckCircle className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
      {gateCode} Gate {passed ? `— ${gateStatus.score}%` : attempted ? '— Retry' : '— Required'}
    </button>
  );
};

// ─── Course Card ──────────────────────────────────────────────────────────────
const CourseCard = ({ course, enrollment, locked, stageConfig, navigate }) => {
  const progress = enrollment?.progress || 0;
  const passed = enrollment?.assessmentPassed;
  const started = progress > 0;

  return (
    <div className={`relative bg-white rounded-xl border shadow-sm transition-all duration-200
      ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer'}
      ${passed ? 'border-green-300 bg-green-50/30' : stageConfig.border}`}
      onClick={() => !locked && enrollment && navigate(`/student/courses/${course._id}/player`)}
    >
      {/* Course number badge */}
      <div className="absolute -top-2.5 left-4">
        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${passed ? 'bg-green-500 text-white' : stageConfig.badge}`}>
          {course.courseNumber || course.courseCode}
        </span>
      </div>

      <div className="p-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 text-sm truncate">{course.title}</h4>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{course.description}</p>
          </div>
          <div className="relative flex-shrink-0">
            <ProgressRing pct={progress} size={44} stroke={4} locked={locked} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700">
              {locked ? <Lock className="w-3 h-3 text-gray-400" /> : `${progress}%`}
            </span>
          </div>
        </div>

        {/* 7-Stage flow mini progress */}
        {enrollment && !locked && (
          <div className="mt-3">
            <div className="flex gap-0.5">
              {['A_Why', 'B_Story', 'C_Framework', 'D_Practice', 'E_FlashCard', 'F_AdvancedPractice', 'H_Notes'].map((stepKey, i) => {
                const done = enrollment.learningFlowProgress?.find(s => s.stepCode === stepKey)?.isCompleted;
                const stepLabels = ['Why', 'Story', 'Framework', 'Practice', 'Flash Cards', 'Advanced Practice', 'Notes'];
                return (
                  <div key={stepKey} title={`${stepLabels[i]} Step`}
                    className={`h-1.5 flex-1 rounded-full transition-all ${done ? 'bg-gradient-to-r ' + stageConfig.gradient : 'bg-gray-200'}`} />
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">7-stage learning flow</p>
          </div>
        )}

        {/* Status indicator */}
        <div className="mt-3 flex items-center justify-between">
          {passed ? (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
              <CheckCircle className="w-3.5 h-3.5" /> Passed — {enrollment?.bestAssessmentScore}%
            </span>
          ) : locked ? (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Lock className="w-3.5 h-3.5" /> Complete previous course
            </span>
          ) : started ? (
            <span className={`text-xs font-medium ${stageConfig.text}`}>Continue →</span>
          ) : (
            <span className="text-xs text-gray-400">Not started</span>
          )}
          {!locked && !passed && (
            <Play className={`w-3.5 h-3.5 ${stageConfig.text}`} />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Track Card ───────────────────────────────────────────────────────────────
const TrackCard = ({ track, trackData, navigate }) => {
  const cfg = TRACK_CONFIG[track.trackCode];
  const Icon = cfg.icon;
  return (
    <div className={`relative bg-white rounded-2xl border-2 shadow-sm overflow-hidden
      ${track.isUnlocked ? cfg.border + ' hover:shadow-md cursor-pointer' : 'border-gray-200 opacity-60 cursor-not-allowed'}`}
      onClick={() => track.isUnlocked && navigate(`/student/tracks/${track.trackCode.toLowerCase()}`)}
    >
      {!track.isUnlocked && (
        <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center z-10 gap-1">
          <Lock className="w-6 h-6 text-gray-400" />
          <p className="text-xs text-gray-500 font-medium">Unlocks after {cfg.unlockAfter}</p>
        </div>
      )}
      <div className={`h-1.5 w-full bg-gradient-to-r ${cfg.color}`} />
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl ${cfg.light} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${cfg.text}`} />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{track.trackCode} Track</p>
            <h4 className="font-bold text-gray-900 text-sm">{cfg.label}</h4>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{track.coursesCompleted}/{track.totalCourses} courses</span>
          <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${cfg.color} rounded-full transition-all`}
              style={{ width: `${(track.coursesCompleted / track.totalCourses) * 100}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const MyCourses = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState('Stage1_Capacity');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const studentId = user._id || user.id;

      const [journeyRes, coursesRes] = await Promise.all([
        api.get(`/programme/student-journey/${studentId}`),
        api.get('/courses?status=active&limit=100')
      ]);

      if (journeyRes.data.success) setJourney(journeyRes.data.data);
      if (coursesRes.data.success) setCourses(coursesRes.data.data || []);
    } catch (err) {
      // If no journey yet (first time), show empty state
      if (err.response?.status !== 404) toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await api.post('/programme/enroll', { studentId: user._id || user.id, collegeId: user.college });
      toast.success('🎉 Enrolled in Human Intelligence Programme!');
      fetchData();
    } catch (err) {
      toast.error('Enrollment failed. Please try again.');
    }
  };

  // Group courses by stage
  const coursesByStage = {
    Stage1_Capacity: courses.filter(c => c.stage === 'Stage1_Capacity' && c.programmeType === 'HumanIntelligence').sort((a, b) => a.orderInStage - b.orderInStage),
    Stage2_Capability: courses.filter(c => c.stage === 'Stage2_Capability' && c.programmeType === 'HumanIntelligence').sort((a, b) => a.orderInStage - b.orderInStage),
    Stage3_Leadership: courses.filter(c => c.stage === 'Stage3_Leadership' && c.programmeType === 'HumanIntelligence').sort((a, b) => a.orderInStage - b.orderInStage)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-500 font-medium">Loading your learning journey…</p>
        </div>
      </div>
    );
  }

  // First-time enrolment state
  if (!journey && courses.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Human Intelligence Programme</h1>
        <p className="text-gray-500 mb-8 text-lg">Begin your 40-course journey. Build capacity, develop capability, lead with purpose.</p>
        <div className="grid grid-cols-3 gap-4 mb-8 text-center">
          {[['25', 'Core Courses', 'text-violet-600'], ['15', 'Track Courses', 'text-blue-600'], ['1', 'Skills Passport', 'text-amber-600']].map(([n, l, c]) => (
            <div key={l} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className={`text-3xl font-black ${c}`}>{n}</p>
              <p className="text-sm text-gray-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
        <button onClick={handleEnroll}
          className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all">
          🚀 Start My Journey
        </button>
      </div>
    );
  }

  const activeStageData = journey?.stages?.find(s => s.stageCode === activeStage);
  const activeCoursesForStage = coursesByStage[activeStage] || [];
  const stageConfig = STAGE_CONFIG[activeStage];
  const StageIcon = stageConfig.icon;

  // Overall stats
  const totalCompleted = (journey?.stages || []).reduce((s, st) => s + (st.coursesCompleted || 0), 0);
  const overallPct = Math.round((totalCompleted / 25) * 100);

  return (
    <div className="space-y-6 pb-12">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-700 to-indigo-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-5 h-5 opacity-80" />
              <span className="text-sm font-medium opacity-80">Human Intelligence Programme</span>
            </div>
            <h1 className="text-2xl font-black">My Learning Journey</h1>
            <p className="text-violet-200 text-sm mt-1">40 Courses · 3 Stages · 3 Tracks · 1 Skills Passport</p>
          </div>
          <div className="text-right">
            <div className="relative w-20 h-20">
              <ProgressRing pct={overallPct} size={80} stroke={7} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-black">{overallPct}%</span>
                <span className="text-[9px] opacity-70">complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stage pills */}
        <div className="flex gap-2 mt-5 flex-wrap">
          {(journey?.stages || []).map(st => {
            const cfg = STAGE_CONFIG[st.stageCode];
            const isActive = activeStage === st.stageCode;
            return (
              <button key={st.stageCode}
                onClick={() => st.isUnlocked && setActiveStage(st.stageCode)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all
                  ${isActive ? 'bg-white text-violet-700 shadow-lg' : st.isUnlocked ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white/10 text-white/50 cursor-not-allowed'}`}
              >
                {st.isUnlocked ? <CheckCircle className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {cfg.label.split('—')[1]?.trim()}
                <span className="bg-white/20 px-1.5 py-0.5 rounded-full">{st.coursesCompleted}/{st.totalCourses}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Active Stage Panel ──────────────────────────────────────── */}
      <div className={`bg-gradient-to-br ${stageConfig.light} rounded-2xl border ${stageConfig.border} p-5 shadow-sm`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-gradient-to-br ${stageConfig.gradient} rounded-xl flex items-center justify-center shadow-md`}>
              <StageIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">{stageConfig.label}</h2>
              <p className="text-sm text-gray-500">{stageConfig.subtitle} · {activeStageData?.coursesCompleted || 0}/{stageConfig.total} completed</p>
            </div>
          </div>

          {/* Gate badge */}
          {activeStageData && (
            <GateBadge
              gateCode={activeStageData.gateCode}
              gateStatus={activeStageData.gateStatus}
              onClick={() => navigate(`/student/gate/${activeStageData.gateCode}`)}
            />
          )}
        </div>

        {/* Course grid */}
        {!activeStageData?.isUnlocked ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
              <Lock className="w-7 h-7 text-gray-400" />
            </div>
            <p className="font-semibold text-gray-700">Stage Locked</p>
            <p className="text-sm text-gray-400 text-center">
              Complete the previous stage and pass the gate assessment to unlock.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeCoursesForStage.map((course, idx) => {
              const enrollment = journey?.enrollmentMap?.[course._id];
              // Lock course if previous not passed (sequential)
              const prevCourse = idx > 0 ? activeCoursesForStage[idx - 1] : null;
              const prevEnrollment = prevCourse ? journey?.enrollmentMap?.[prevCourse._id] : null;
              const locked = idx > 0 && !prevEnrollment?.assessmentPassed;
              return (
                <CourseCard key={course._id} course={course} enrollment={enrollment}
                  locked={locked} stageConfig={stageConfig} navigate={navigate} />
              );
            })}
            {activeCoursesForStage.length === 0 && (
              <div className="col-span-full text-center py-10 text-gray-400">
                <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No courses published for this stage yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Parallel Tracks ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-bold text-gray-900">Readiness Tracks</h2>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">Parallel · 15 courses total</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {(journey?.tracks || []).map(track => (
            <TrackCard key={track.trackCode} track={track} trackData={TRACK_CONFIG[track.trackCode]} navigate={navigate} />
          ))}
        </div>
      </div>

      {/* ── Skills Passport Teaser ──────────────────────────────────── */}
      <div
        onClick={() => journey?.passportUnlocked ? navigate('/student/passport') : null}
        className={`bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-xl
          ${journey?.passportUnlocked ? 'cursor-pointer hover:shadow-2xl hover:-translate-y-0.5 transition-all' : 'opacity-70'}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-5 h-5" />
              <span className="font-bold text-sm">Skills Passport</span>
            </div>
            {journey?.passport ? (
              <>
                <p className="text-3xl font-black">{journey.passport.SRIScore} <span className="text-lg font-semibold opacity-80">SRI</span></p>
                <p className="text-amber-100 text-sm">{journey.passport.SRILevel} · View your full passport →</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-black">SRI Score: 300–900</p>
                <p className="text-amber-100 text-sm">Complete all 25 core courses + T4 gate to unlock</p>
              </>
            )}
          </div>
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
            {journey?.passportUnlocked ? <Trophy className="w-9 h-9" /> : <Lock className="w-8 h-8" />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCourses;
